import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { gql, useQuery } from '@apollo/client';
import { styled } from 'linaria/react';

import Avatar from 'components/UI/Avatar/Avatar';
import useTheme from 'helpers/useTheme';
import { COLORS, FONTS } from 'styles/constants';
import { CardPickSchema, CardSchema, UserObjectType } from 'constants/graphqlTypes';
import { EQueryFetchPolicy } from 'constants/common';
import { useGetUserCards } from 'graphQL/cards/userCards/hooks';
import getStyles from 'helpers/getStyles';
import Grain from 'components/UI/Grain';
import LoaderContent from 'components/UI/LoaderContent/LoaderContent';
// Slider is used inside UserRecentPicks, not for avatars
import UserPicksImmersive from 'components/ImmersiveCover/UserPicksImmersive';
import { StyledFillButton } from 'components/Navigation/styled';
import { ReactComponent as ChevronLeft } from 'assets/icons/ChevronLeft.svg';
import UserPickDetailMenu from 'components/Navigation/BottomMenu/UserPickDetailMenu';

const LATEST_FOLLOWING_PICKS = gql`
  query LatestFollowingPicks($limit: Int, $offset: Int) {
    latestFollowingPicks(limit: $limit, offset: $offset) {
      items {
        id
        createdAt
        card {
          createdAt
          extraData {
            items {
              __typename
              ... on CollectionCardLinkSchema {
                id
                createdAt
                createdAtStamp
                pickCreatedAt
              }
            }
          }
        }
        user {
          id
          username
          userInfo {
            firstName
            lastName
          }
          userStyle {
            thumbnailImageSet {
              images {
                id
                url
                imageType
              }
            }
          }
        }
      }
    }
  }
`;

const StyledContainer = styled.div`
  display: flex;
  padding: 12px 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  align-self: stretch;
`;

const StyledTitle = styled.div<{ isDark: boolean }>`
  font-family: ${FONTS.secondary};
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
  color: ${({ isDark }) => (isDark ? COLORS.white[100] : 'var(--Dark-brown, #181800)')};
`;

const StyledAvatarsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const StyledAvatarItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
`;

const StyledAvatarRing = styled.div<{ visited?: boolean }>`
  display: flex;
  width: 76px;
  height: 76px;
  padding: 6px;
  justify-content: center;
  align-items: center;
  border-radius: 999px;
  border: 2px solid ${({ visited }) => (visited ? 'var(--White, #FFFFFF)' : 'var(--Green, #BDD900)')};
`;

const StyledUsername = styled.div<{ isDark: boolean }>`
  overflow: hidden;
  color: ${({ isDark }) => (isDark ? COLORS.white[100] : 'var(--Dark-brown, #181800)')};
  text-align: center;
  font-feature-settings: 'liga' off, 'clig' off;
  text-overflow: ellipsis;
  font-family: ${FONTS.secondary};
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
`;

const StyledOverlay = styled.div<{ isOpen: boolean; bg?: string }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: ${({ bg }) => bg ?? 'var(--dark-brown, #181800)'};
  z-index: 9000000;
  transform: translateX(${({ isOpen }) => (isOpen ? '0' : '100%')});
  transition: transform 220ms ease-in-out, background 220ms ease-in-out;
  display: flex;
  flex-direction: column;
`;

const StyledOverlayHeader = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  align-items: center;
  padding: 12px;
  background-color: transparent;
  z-index: 9500000;
  pointer-events: auto;
`;

const StyledOverlayBottom = styled.div`
  position: absolute;
  left: 50%;
  bottom: 12px;
  transform: translateX(-50%);
  width: 100%;
  display: flex;
  justify-content: center;
  pointer-events: auto;
  z-index: 9500000;
`;

// Title is currently omitted from the overlay header; add back if needed

//

const UserRecentPicks: FC<{
  username: string;
  onBgUpdate: (bg: string) => void;
  onActiveCardChange?: (card: CardSchema | null) => void;
  defaultCreatedAt?: string;
  onClose: () => void;
}> = ({ username, onBgUpdate, onActiveCardChange, defaultCreatedAt, onClose }) => {
  const { recentCards, loading } = useGetUserCards({ username });
  const [activeCard, setActiveCard] = useState<CardSchema | null>(null);

  useEffect(() => {
    if (recentCards && recentCards.length > 0) {
      const { bgColor } = getStyles(recentCards[0].cardStyle);
      onBgUpdate(bgColor);
      setActiveCard(recentCards[0]);
    } else {
      setActiveCard(null);
    }
  }, [recentCards, onBgUpdate]);

  useEffect(() => {
    onActiveCardChange?.(activeCard);
  }, [activeCard, onActiveCardChange]);

  return (
    <>
      <StyledOverlayHeader>
        <StyledFillButton isDark onClick={onClose}>
          <ChevronLeft />
        </StyledFillButton>
      </StyledOverlayHeader>
      {loading ? (
        <LoaderContent isDark isCentered />
      ) : (
        <UserPicksImmersive
          cards={recentCards ?? []}
          defaultCreatedAt={defaultCreatedAt}
          onBgUpdate={onBgUpdate}
          onActiveChange={(card) => setActiveCard(card)}
        />
      )}
    </>
  );
};

type LatestPickUser = {
  user: UserObjectType;
  createdAt?: string | null;
};

const LatestPicks: FC = () => {
  const isDark = useTheme();
  const [openUserContext, setOpenUserContext] = useState<{ username: string; createdAt?: string | null } | null>(null);
  const [overlayBg, setOverlayBg] = useState<string | undefined>(undefined);
  const [activeCard, setActiveCard] = useState<CardSchema | null>(null);
  const handleBgUpdate = useCallback((bg: string) => {
    setOverlayBg(bg);
  }, []);
  useEffect(() => {
    if (!openUserContext) {
      setActiveCard(null);
    }
  }, [openUserContext]);
  const VISITED_KEY = 'latestPicksVisitedUserIds';
  const [visitedIds, setVisitedIds] = useState<Set<string>>(() => {
    try {
      const raw = sessionStorage.getItem(VISITED_KEY);
      const arr = raw ? (JSON.parse(raw) as string[]) : [];
      return new Set(arr);
    } catch {
      return new Set<string>();
    }
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(VISITED_KEY, JSON.stringify(Array.from(visitedIds)));
    } catch {
      // no-op if storage unavailable
    }
  }, [visitedIds]);

  const markVisited = useCallback((id?: string | null) => {
    if (!id) return;
    setVisitedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      // persist synchronously to survive immediate navigation
      try {
        sessionStorage.setItem(VISITED_KEY, JSON.stringify(Array.from(next)));
      } catch {
        // ignore storage errors
      }
      return next;
    });
  }, []);

  const { data } = useQuery(LATEST_FOLLOWING_PICKS, {
    variables: { limit: 20, offset: 0 },
    fetchPolicy: EQueryFetchPolicy.CacheAndNetwork,
  });

  const latestPickUsers: LatestPickUser[] = useMemo(() => {
    const items: CardPickSchema[] = data?.latestFollowingPicks?.items ?? [];
    const seen = new Set<string>();
    const users: LatestPickUser[] = [];
    items.forEach((pick) => {
      const user = pick?.user as UserObjectType | undefined;
      if (!user?.id) return;
      if (!seen.has(user.id)) {
        seen.add(user.id);
        users.push({ user, createdAt: pick?.createdAt });
      }
    });
    return users;
  }, [data]);

  if (!latestPickUsers.length) return null;

  return (
    <StyledContainer>
      <StyledAvatarsRow>
        {latestPickUsers.map(({ user, createdAt }) => (
          <StyledAvatarItem key={user.id}>
            <StyledAvatarRing
              visited={visitedIds.has(user.id)}
              onMouseDown={() => markVisited(user.id)}
              onClick={() => {
                markVisited(user.id);
                setOpenUserContext({ username: user.username, createdAt });
              }}
            >
              <Avatar user={user} dimension={64} />
            </StyledAvatarRing>
            <StyledUsername isDark={isDark}>{user?.username ? `:${user.username}` : ''}</StyledUsername>
          </StyledAvatarItem>
        ))}
      </StyledAvatarsRow>
      {createPortal(
        <StyledOverlay isOpen={!!openUserContext} bg={overlayBg}>
          <Grain />
          {openUserContext && (
            <UserRecentPicks
              username={openUserContext.username}
              defaultCreatedAt={openUserContext.createdAt ?? undefined}
              onBgUpdate={handleBgUpdate}
              onActiveCardChange={(card) => setActiveCard(card)}
              onClose={() => setOpenUserContext(null)}
            />
          )}
          {activeCard && (
            <StyledOverlayBottom>
              <UserPickDetailMenu card={activeCard} />
            </StyledOverlayBottom>
          )}
        </StyledOverlay>,
        document.body
      )}
    </StyledContainer>
  );
};

export default LatestPicks;
